import { useEffect, useMemo, useState } from 'react';
import Grid from '@mui/material/Grid2';
import moment from 'moment-timezone';

import { AttributionPerformance } from '../../types/CampaignPerformance';
import { Metric } from '../../types/Analytics/Metric';
import MetricCard, { setupMetricChange } from '../../components/Metrics/MetricCard';
import { useAttributionPageContext } from '../../hooks/useAttributionPage';
import { useAmcReachAndFrequencyList } from '../../hooks/useAmcReachAndFrequencyList';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import ApiService from '../../ApiService';
import { SalesDataUpload } from '../../types/SalesDataUpload';

export default function AdvertiserMatchbackMetrics() {
    const {
        attributionCriteria,
        attributionDate,
        performances,
        previousPerformances,
        attributionMetrics,
        setAttributionMetrics,
        isFetchingPerformances,
        isFetchingPreviousPerformances,

        attributionPerformances,
        fetchAttributionPerformances,
        isLoadingAttributionPerformances,
        previousAttributionPerformances,
        fetchPreviousAttributionPerformances,
        isLoadingPreviousAttributionPerformances,
    } = useAttributionPageContext();

    const { amcReachAndFrequencyList, fetchAmcReachAndFrequencyList, isFetchingAmcReachAndFrequencyList } =
        useAmcReachAndFrequencyList();

    const {
        amcReachAndFrequencyList: previousAmcReachAndFrequencyList,
        fetchAmcReachAndFrequencyList: fetchPreviousAmcReachAndFrequencyList,
        isFetchingAmcReachAndFrequencyList: isFetchingPreviousAmcReachAndFrequencyList,
    } = useAmcReachAndFrequencyList();

    function getSumTotalCampaignPerformanceMetric(_metrics: any[], key: string): number {
        return _metrics.map((_metric: any) => _metric?.[key] ?? 0).reduce((_total, _subtotal) => _total + _subtotal, 0);
    }

    const [sourceFilesOpen, setSourceFilesOpen] = useState(false);
    const [sourceFiles, setSourceFiles] = useState<SalesDataUpload[] | []>([]); // List of source files
    const [isLoadingSourceFiles, setIsLoadingSourceFiles] = useState(false);

    async function fetchSourceFiles() {
        setIsLoadingSourceFiles(true);

        try {
            // Simulated request — replace with your actual API call
            // Adjust URL
            const response = await ApiService.searchSalesDataUploads(attributionCriteria, attributionDate);

            setSourceFiles(response.data);
        } catch (error) {
            setSourceFiles([]);
        } finally {
            setIsLoadingSourceFiles(false);
        }
    }

    const getMetricByAttributionPerformance = (
        target: string,
        format: 'number' | 'dollar' | 'decimal' | 'percent'
    ): Metric => {
        let _metric: Metric = {
            format: format,
            value: 0,
        };

        if (attributionPerformances && attributionPerformances.length) {
            _metric.value = attributionPerformances
                .map((o: AttributionPerformance) => o[target as keyof AttributionPerformance] as number)
                .reduce((a, b) => a + b, 0);
        }

        if (previousAttributionPerformances && previousAttributionPerformances.length) {
            _metric.previousValue = previousAttributionPerformances
                .map((o: AttributionPerformance) => o[target as keyof AttributionPerformance] as number)
                .reduce((a, b) => a + b, 0);
        }

        _metric = setupMetricChange(_metric);

        return _metric;
    };

    const getMetricByCampaignPerformance = (
        target: string,
        format: 'number' | 'dollar' | 'decimal' | 'percent'
    ): Metric => {
        let _metric: Metric = {
            format: format,
            value: 0,
        };

        if (performances.length > 0) {
            _metric.value = getSumTotalCampaignPerformanceMetric(performances, target);
        }

        if (previousPerformances.length > 0) {
            _metric.previousValue = getSumTotalCampaignPerformanceMetric(previousPerformances, target);
        }

        _metric = setupMetricChange(_metric);

        return _metric;
    };

    const totalSales = useMemo(() => {
        return getMetricByAttributionPerformance('totalSales', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributionPerformances, previousAttributionPerformances]);

    const attributedSales = useMemo(() => {
        return getMetricByAttributionPerformance('totalAdExposedSales', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributionPerformances, previousAttributionPerformances]);

    const totalSpend = useMemo(() => {
        return getMetricByCampaignPerformance('totalCost', 'dollar');
        // return getMetricByAttributionPerformance('totalInvestment', 'dollar');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [performances, previousPerformances]);
    //[attributionPerformances, previousAttributionPerformances]);

    const costPerSold = useMemo(() => {
        return getMetricByAttributionPerformance('costPerAdExposedSale', 'dollar');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributionPerformances, previousAttributionPerformances]);

    const totalImpressions = useMemo(() => {
        return getMetricByCampaignPerformance('impressions', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [performances, previousPerformances]);

    const totalClickthroughs = useMemo(() => {
        return getMetricByCampaignPerformance('clickthroughs', 'number');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [performances, previousPerformances]);

    const totalDailyReach: Metric = useMemo(() => {
        return setupMetricChange({
            format: 'number',
            value: amcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.reach as number)
                .reduce((a: number, b: number) => a + b, 0),
            previousValue: previousAmcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.reach as number)
                .reduce((a: number, b: number) => a + b, 0),
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [amcReachAndFrequencyList, previousAmcReachAndFrequencyList]);

    const totalImpressionFrequency: Metric = useMemo(() => {
        let _metric: Metric = {
            format: 'number',
            value: 0,
        };

        if (amcReachAndFrequencyList.length > 0) {
            _metric.value = amcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.frequency as number)
                .reduce((a: number, b: number) => a + b, 0);
            _metric.value = Math.round(_metric.value / amcReachAndFrequencyList.length);
        }

        if (previousAmcReachAndFrequencyList.length > 0) {
            _metric.previousValue = previousAmcReachAndFrequencyList
                .map((amcReachAndFrequency: any) => amcReachAndFrequency?.frequency as number)
                .reduce((a: number, b: number) => a + b, 0);
            _metric.previousValue = Math.round(_metric.previousValue / previousAmcReachAndFrequencyList.length);
        }

        _metric = setupMetricChange(_metric);

        return _metric;
    }, [amcReachAndFrequencyList, previousAmcReachAndFrequencyList]);

    const totalSpendMetricLabel: string = useMemo(() => {
        if (attributionDate) {
            return `${moment(attributionDate).format('MMMM')} Investment`;
        }

        return 'Total Investment';
    }, [attributionDate]);

    const costPerSoldTooltip: string = useMemo(() => {
        if (attributionDate) {
            return '{month} Investment divided by Total Ad Exposed Sales'.replace(
                '{month}',
                moment(attributionDate).format('MMMM')
            );
        }

        return 'Total Investment divided by Total Ad Exposed Sales';
    }, [attributionDate]);

    useEffect(() => {
        if (attributionCriteria.advertiserIds.length > 0 && attributionDate) {
            const startDate: Date = moment(attributionDate).startOf('month').toDate();
            const endDate: Date = moment(attributionDate).endOf('month').toDate();

            const previousStartDate: Date = moment
                .utc(moment(attributionDate).subtract(1, 'month').format('YYYY-MM-DD HH:mm:ss'))
                .toDate();
            const previousEndDate: Date = moment
                .utc(moment(attributionDate).subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss'))
                .toDate();

            fetchAmcReachAndFrequencyList({
                dealerIds: attributionCriteria.advertiserIds,
                campaignName: attributionCriteria.dataSet !== 'Sales' ? null : 'Global',
                dataSet: attributionCriteria?.dataSet !== 'Sales' ? attributionCriteria.dataSet : null,
                startDate: startDate,
                endDate: endDate,
            });

            fetchPreviousAmcReachAndFrequencyList({
                dealerIds: attributionCriteria.advertiserIds,
                campaignName: attributionCriteria.dataSet !== 'Sales' ? null : 'Global',
                dataSet: attributionCriteria?.dataSet !== 'Sales' ? attributionCriteria.dataSet : null,
                startDate: previousStartDate,
                endDate: previousEndDate,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributionCriteria, attributionDate]);

    useEffect((): void => {
        fetchAttributionPerformances();
    }, [fetchAttributionPerformances]);

    useEffect((): void => {
        fetchPreviousAttributionPerformances();
    }, [fetchPreviousAttributionPerformances]);

    useEffect(() => {
        setAttributionMetrics({
            ...attributionMetrics,
            ...{
                totalSales,
                attributedSales,
                totalSpend,
                costPerSold,
                totalImpressions,
                totalClickthroughs,
                totalImpressionFrequency,
                totalDailyReach,
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        totalSales,
        attributedSales,
        totalSpend,
        costPerSold,
        totalImpressions,
        totalClickthroughs,
        totalImpressionFrequency,
        totalDailyReach,
    ]);

    const handleOpenSourceFiles = () => {
        setSourceFilesOpen(true);
        fetchSourceFiles(); // Trigger fetch
    };

    const handleCloseSourceFiles = () => {
        setSourceFilesOpen(false);
    };

    return (
        <>
            <Grid container spacing={2}>
                <Grid size={3}>
                    <MetricCard
                        loading={isLoadingAttributionPerformances || isLoadingPreviousAttributionPerformances}
                        metric={totalSales}
                        label={`Total ${attributionCriteria.dataSet ?? 'Sales'}`}
                        tooltip="Total Advertiser Sales as reported by Advertiser"
                        action={
                            <Button
                                variant="text"
                                size="small"
                                onClick={handleOpenSourceFiles}
                                sx={{ textTransform: 'none', fontSize: 12, padding: 0, minWidth: 'auto' }}
                            >
                                Source Files
                            </Button>
                        }
                    />
                </Grid>
                <Grid size={3}>
                    <MetricCard
                        loading={isLoadingAttributionPerformances || isLoadingPreviousAttributionPerformances}
                        metric={attributedSales}
                        label={`Total Ad Exposed ${attributionCriteria.dataSet ?? 'Sales'}`}
                        tooltip="Total households that were exposed to an advertisement and made a purchase for the selected time period"
                    />
                </Grid>
                <Grid size={3}>
                    <MetricCard
                        loading={isFetchingPerformances || isFetchingPreviousPerformances}
                        metric={totalSpend}
                        label={totalSpendMetricLabel}
                        tooltip="Total advertiser ad spend for the selected time period"
                    />
                </Grid>
                <Grid size={3}>
                    <MetricCard
                        loading={isLoadingAttributionPerformances || isLoadingPreviousAttributionPerformances}
                        metric={costPerSold}
                        label={`Cost Per ${attributionCriteria.dataSet ?? 'Sales'}`}
                        tooltip={costPerSoldTooltip}
                        inverseColor={true}
                    />
                </Grid>
                <Grid size={3}>
                    <MetricCard
                        loading={isFetchingPerformances || isFetchingPreviousPerformances}
                        metric={totalImpressions}
                        label="Total Exposures"
                        tooltip="Total number of ad exposures or impressions for the selected advertiser for the selected time period"
                    />
                </Grid>
                <Grid size={3}>
                    <MetricCard
                        loading={isFetchingPerformances || isFetchingPreviousPerformances}
                        metric={totalClickthroughs}
                        label="Total Clicks"
                        tooltip="Total number of clicks on an advertisement for the selected time period"
                    />
                </Grid>
                <Grid size={3}>
                    <MetricCard
                        loading={isFetchingAmcReachAndFrequencyList || isFetchingPreviousAmcReachAndFrequencyList}
                        metric={totalDailyReach}
                        label="Household Reach"
                        tooltip="Total number of households exposed to an advertisement"
                    />
                </Grid>
                <Grid size={3}>
                    <MetricCard
                        loading={isFetchingAmcReachAndFrequencyList || isFetchingPreviousAmcReachAndFrequencyList}
                        metric={totalImpressionFrequency}
                        label="Avg. Household Frequency"
                        tooltip="Avg. number of times a household saw advertisements across all media types and channels"
                        inverseColor={true}
                    />
                </Grid>
            </Grid>
            <Dialog open={sourceFilesOpen} onClose={handleCloseSourceFiles} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        Source Files
                    </Typography>
                </DialogTitle>

                <DialogContent dividers>
                    {isLoadingSourceFiles ? (
                        <Typography>Loading...</Typography>
                    ) : (
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Date Range</TableCell>
                                    <TableCell>File Name</TableCell>
                                    <TableCell>Uploaded Date</TableCell>
                                    <TableCell>Uploaded By</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {sourceFiles.length > 0 ? (
                                    sourceFiles?.map((row, idx) => (
                                        <TableRow key={idx}>
                                            <TableCell>
                                                {new Date(row.beginDate).toLocaleDateString()} -{' '}
                                                {new Date(row.endDate).toLocaleDateString()}
                                            </TableCell>
                                            <TableCell>{row.originalFileName}</TableCell>
                                            <TableCell>{new Date(row.dateUploaded).toLocaleDateString()}</TableCell>
                                            <TableCell>{row.uploadedByName}</TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={4}>No source files found.</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>

                <DialogActions>
                    <Button onClick={handleCloseSourceFiles} variant="contained">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
